export class ErrorCapture {
  constructor(devtools) {
    this.devtools = devtools;
    this.originalErrorHandler = null;
    this.originalUnhandledRejection = null;
  }

  init() {
    this.setupGlobalErrorHandlers();
    this.setupConsoleInterception();
  }

  setupGlobalErrorHandlers() {
    // Capture JavaScript errors
    this.originalErrorHandler = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      this.handleError({
        type: 'javascript',
        message,
        source,
        lineno,
        colno,
        error,
        timestamp: Date.now()
      });

      if (this.originalErrorHandler) {
        return this.originalErrorHandler(message, source, lineno, colno, error);
      }
      return false;
    };

    // Capture unhandled promise rejections
    this.originalUnhandledRejection = window.onunhandledrejection;
    window.addEventListener('unhandledrejection', (event) => {
      this.handleError({
        type: 'promise',
        message: event.reason?.message || 'Unhandled Promise Rejection',
        error: event.reason,
        timestamp: Date.now()
      });

      if (this.originalUnhandledRejection) {
        this.originalUnhandledRejection(event);
      }
    });
  }

  setupConsoleInterception() {
    const originalConsole = { ...console };
    
    ['error', 'warn', 'log'].forEach(method => {
      console[method] = (...args) => {
        if (method === 'error') {
          this.handleError({
            type: 'console',
            message: args.join(' '),
            timestamp: Date.now(),
            level: method
          });
        }

        this.devtools.runtimeManager.logOperation({
          type: 'console',
          data: { method, args }
        });

        originalConsole[method](...args);
      };
    });
  }

  async handleError(errorData) {
    const codeSnippet = await this.extractCodeSnippet(errorData);
    
    const enrichedError = {
      ...errorData,
      id: `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      codeSnippet,
      stackTrace: errorData.error?.stack?.split('\n') || []
    };

    // Store error in runtime manager
    this.devtools.runtimeManager.errors = this.devtools.runtimeManager.errors || [];
    this.devtools.runtimeManager.errors.push(enrichedError);

    // Perform deep analysis if CodeInspector is available
    if (this.devtools.codeInspector) {
      try {
        enrichedError.analysis = await this.devtools.codeInspector.analyzeError(enrichedError);
      } catch (analysisError) {
        console.warn('Error analysis failed:', analysisError);
      }
    }

    this.devtools.windowManager.createErrorWindow(enrichedError);
    this.devtools.runtimeManager.logOperation({
      type: 'error',
      data: enrichedError
    });
  }

  async extractCodeSnippet(errorData) {
    try {
      // Try to get the actual source code
      if (errorData.source && errorData.lineno) {
        const sourceCode = await this.fetchSourceCode(errorData.source);
        if (sourceCode) {
          return this.extractLinesAroundError(sourceCode, errorData.lineno, errorData.colno);
        }
      }

      // Fallback: try to extract from stack trace
      if (errorData.stackTrace && errorData.stackTrace.length > 0) {
        const stackLine = errorData.stackTrace[0];
        const match = stackLine.match(/at\s+.*\s+\((.+):(\d+):(\d+)\)/);
        if (match) {
          const [, source, line, col] = match;
          const sourceCode = await this.fetchSourceCode(source);
          if (sourceCode) {
            return this.extractLinesAroundError(sourceCode, parseInt(line), parseInt(col));
          }
        }
      }

      // Last resort: try to get current page source
      if (errorData.source === window.location.href || !errorData.source) {
        return this.extractFromCurrentPage(errorData.lineno);
      }

    } catch (error) {
      console.warn('Failed to extract code snippet:', error);
    }

    // Fallback to mock if all else fails
    return {
      lines: [
        '// Unable to extract source code',
        `// Error: ${errorData.message}`,
        '// Check browser dev tools for more details'
      ],
      errorLine: 2,
      extracted: false
    };
  }

  async fetchSourceCode(sourceUrl) {
    try {
      // Handle relative URLs
      const url = new URL(sourceUrl, window.location.origin);
      
      // Don't fetch external sources for security
      if (url.origin !== window.location.origin) {
        return null;
      }

      const response = await fetch(url.href);
      if (response.ok) {
        return await response.text();
      }
    } catch (error) {
      console.warn('Failed to fetch source:', error);
    }
    return null;
  }

  extractLinesAroundError(sourceCode, lineNumber, columnNumber = 0) {
    const lines = sourceCode.split('\n');
    const errorLineIndex = lineNumber - 1; // Convert to 0-based index
    
    // Extract 5 lines before and after the error
    const startLine = Math.max(0, errorLineIndex - 5);
    const endLine = Math.min(lines.length - 1, errorLineIndex + 5);
    
    const extractedLines = [];
    for (let i = startLine; i <= endLine; i++) {
      extractedLines.push({
        number: i + 1,
        content: lines[i] || '',
        isError: i === errorLineIndex
      });
    }

    return {
      lines: extractedLines.map(line => 
        `${line.number.toString().padStart(4, ' ')} | ${line.content}`
      ),
      errorLine: errorLineIndex - startLine + 1,
      extracted: true,
      columnNumber
    };
  }

  extractFromCurrentPage(lineNumber) {
    try {
      // Try to get inline script content
      const scripts = document.querySelectorAll('script:not([src])');
      let currentLine = 1;
      
      for (const script of scripts) {
        const scriptLines = script.textContent.split('\n');
        const scriptEndLine = currentLine + scriptLines.length - 1;
        
        if (lineNumber >= currentLine && lineNumber <= scriptEndLine) {
          const relativeLineNumber = lineNumber - currentLine;
          return this.extractLinesFromArray(scriptLines, relativeLineNumber);
        }
        
        currentLine = scriptEndLine + 1;
      }
    } catch (error) {
      console.warn('Failed to extract from current page:', error);
    }

    return {
      lines: [
        '// Unable to locate source in current page',
        `// Error occurred at line ${lineNumber}`,
        '// Source may be in external file or dynamically generated'
      ],
      errorLine: 2,
      extracted: false
    };
  }

  extractLinesFromArray(lines, errorLineIndex) {
    const startLine = Math.max(0, errorLineIndex - 3);
    const endLine = Math.min(lines.length - 1, errorLineIndex + 3);
    
    const extractedLines = [];
    for (let i = startLine; i <= endLine; i++) {
      extractedLines.push(`${(i + 1).toString().padStart(4, ' ')} | ${lines[i] || ''}`);
    }

    return {
      lines: extractedLines,
      errorLine: errorLineIndex - startLine + 1,
      extracted: true
    };
  }

  destroy() {
    if (this.originalErrorHandler) {
      window.onerror = this.originalErrorHandler;
    }
    if (this.originalUnhandledRejection) {
      window.onunhandledrejection = this.originalUnhandledRejection;
    }
  }
}